import { useEffect, useRef, type ReactNode } from 'react';

import { TabOrientation } from '@/lib/types';

import { Box, Grid2, Tabs, type GridProps } from '@/primitives';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

export interface Section {
  title: ReactNode;
  value: string;
}

export interface MultiSectionLayoutProps {
  children?: ReactNode;
  contentSx?: GridProps['sx'];
  currentSection?: string;
  header?: ReactNode;
  onClickSection?: (section: string) => void;
  sections: Section[];
  sectionsFooter?: ReactNode;
}

export function MultiSectionLayout({
  children,
  contentSx,
  currentSection,
  header,
  onClickSection,
  sections,
  sectionsFooter,
}: MultiSectionLayoutProps) {
  const startLength = useRef(window.history.length);

  // browser go back button will navigate back to the previous page instead of the previous section
  useEffect(() => {
    function handleBackButton(event: PopStateEvent) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.history.go(-(window.history.length - startLength.current));
    }

    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {header}
      <Grid2 container direction="row">
        <Grid2 item xs={3}>
          <Tabs
            onClick={onClickSection}
            orientation={TabOrientation.Vertical}
            tabs={
              sections?.map((section) => ({
                label: section.title,
                value: section.value,
              })) || []
            }
            value={currentSection || ''}
          />
          {sectionsFooter}
        </Grid2>
        <Grid2 item xs={9} sx={contentSx}>
          {children}
        </Grid2>
      </Grid2>
    </Box>
  );
}
